import { useEffect, useState } from "react";
import { fetchAudienceIntelligenceResolution } from "../services/audienceIntelligenceService";

/*
 * Status-aware fetch of the canonical audience-intelligence resolution for
 * the redesigned platform analytics pages.
 *
 * This intentionally does NOT modify the shared useAudienceIntelligenceResolution
 * hook (still used by ConnectedPlatformAnalyticsPage and
 * ProgrammaticChannelAnalyticsPage): the redesigned pages need to distinguish
 * "still resolving" from "resolved to nothing" and "request failed" so each
 * state renders honestly. Same canonical API, same service function — only
 * request-state tracking is added.
 *
 * Refresh model (consumer-only, no polling): the request re-runs when the
 * advertiser, platform, campaign, or analysis window changes and on route
 * re-entry (mount). Analytics pages never write or trigger synthesis.
 */
export default function usePlatformAudienceSynthesis({
  advertiserId,
  platform,
  campaignId = null,
  campaignName = null,
  startDate = null,
  endDate = null,
  enabled = true,
}) {
  const [state, setState] = useState({ requestState: "idle", resolution: null });

  useEffect(() => {
    if (!enabled || !advertiserId || !platform) {
      setState({ requestState: "idle", resolution: null });
      return undefined;
    }

    const controller = new AbortController();
    let cancelled = false;
    setState({ requestState: "loading", resolution: null });

    fetchAudienceIntelligenceResolution(
      { advertiserId, platform, campaignId, campaignName, startDate, endDate },
      { signal: controller.signal }
    )
      .then((payload) => {
        if (cancelled) return;
        setState({ requestState: "resolved", resolution: payload || null });
      })
      .catch((error) => {
        if (controller.signal.aborted || cancelled) return;
        console.warn(
          "[usePlatformAudienceSynthesis] unable to resolve audience intelligence",
          error
        );
        setState({ requestState: "error", resolution: null });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [advertiserId, campaignId, campaignName, enabled, endDate, platform, startDate]);

  return state;
}
